import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/reviews`);
        setReviews(res.data);
      } catch (err) {
        console.error("Failed to load reviews:", err);
      }
    };
    fetchReviews();
  }, []);

  const next = () => setCurrentIdx((prev) => (prev + 1) % reviews.length);
  const prev = () => setCurrentIdx((prev) => (prev - 1 + reviews.length) % reviews.length);

  if (!reviews.length) return null;

  const review = reviews[currentIdx];

  return (
    <section
      id="reviews"
      className="py-24 md:py-32 bg-brand-surface relative noise-overlay"
      data-testid="reviews-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-sans text-brand-gold mb-4">
            Testimonials
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-text tracking-tight">
            What Our Clients Say
          </h2>
          <div className="gold-line-long mx-auto mt-6" />
        </motion.div>

        {/* Review Carousel */}
        <div className="max-w-3xl mx-auto">
          <motion.div
            key={currentIdx}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            {/* Stars */}
            <div className="flex items-center justify-center gap-1 mb-6" data-testid="review-stars">
              {Array.from({ length: review.rating }).map((_, i) => (
                <Star key={i} size={20} className="fill-brand-gold text-brand-gold" />
              ))}
            </div>

            {/* Quote */}
            <p className="font-serif text-xl md:text-2xl text-brand-text leading-relaxed mb-8 italic">
              "{review.text}"
            </p>

            {/* Author */}
            <p className="font-sans text-sm font-semibold text-brand-gold tracking-wide">
              {review.name}
            </p>
            <p className="font-sans text-xs text-brand-text-muted mt-1">
              {review.service}
            </p>
          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-6 mt-10">
            <button
              onClick={prev}
              data-testid="review-prev-btn"
              className="w-10 h-10 border border-brand-border flex items-center justify-center text-brand-text-muted hover:text-brand-gold hover:border-brand-gold transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="font-sans text-sm text-brand-text-muted">
              {currentIdx + 1} / {reviews.length}
            </span>
            <button
              onClick={next}
              data-testid="review-next-btn"
              className="w-10 h-10 border border-brand-border flex items-center justify-center text-brand-text-muted hover:text-brand-gold hover:border-brand-gold transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
